// services/profileExtractionService.ts
// Service for extracting structured data from raw onboarding Q&A using AI
// Uses Mentor Gateway to analyze answers and extract goals, interests, challenges, etc.

import { mentorChatService } from './mentorChatService';
import { PrivateProfileService } from './privateProfileService';
import { PrivateProfile, OnboardingAnswer, DailyRecommendations } from '../models/PrivateProfile';
import { OnboardingPhase1Schema } from '../utils/mentorSchemas';
import { 
  buildPhase1Messages, 
  deriveAudiencesFromAnswers, 
  getVectorRecommendations, 
  OnboardingQA 
} from './onboardingRecommendationService';

/**
 * JSON Schema for AI-extracted profile data
 * Must be plain JSON Schema format for Mentor Gateway
 */
const ExtractedProfileDataSchema = {
  type: 'object',
  properties: {
    goals: {
      type: 'array',
      items: { type: 'string' },
      description: 'User\'s main goals and objectives'
    },
    interests: {
      type: 'array',
      items: { type: 'string' },
      description: 'Topics and areas the user is interested in'
    },
    challenges: {
      type: 'array',
      items: { type: 'string' },
      description: 'Challenges or obstacles the user faces'
    },
    personalityTraits: {
      type: 'array',
      items: { type: 'string' },
      description: 'Key personality traits observed'
    },
    summary: {
      type: 'string',
      description: 'A concise summary of the user based on their responses'
    },
    currentFocus: {
      type: 'array',
      items: { type: 'string' },
      description: 'Their immediate 1-3 priority areas',
      minItems: 1,
      maxItems: 3
    }
  },
  required: ['goals', 'interests', 'challenges', 'personalityTraits', 'summary', 'currentFocus'],
  additionalProperties: false
} as const;

type ExtractedProfileData = {
  goals: string[];
  interests: string[];
  challenges: string[];
  personalityTraits: string[];
  summary: string;
  currentFocus: string[];
};

export class ProfileExtractionService {

  /**
   * Generate a welcome summary using AI analysis
   * Useful when we want a natural language summary instead of bullet points
   */
  static async generateWelcomeSummary(
    onboardingAnswers: OnboardingAnswer[]
  ): Promise<{ success: boolean; summary?: string; error?: string }> {
    try {
      const qaText = onboardingAnswers
        .map(qa => `Q: ${qa.questionText}\nA: ${qa.answer}`)
        .join('\n\n');

      const messages = [
        {
          role: 'user' as const,
          content: `Based on these onboarding responses, write a warm, personalized welcome message (2-3 sentences) that shows you understand the user:

${qaText}

Make it personal and encouraging. Focus on their main goals and show you're ready to help.`,
        },
      ];

      const text = await mentorChatService.sendText(messages);

      return { success: true, summary: text };
    } catch (error: any) {
      console.error('❌ Error generating welcome summary:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate welcome summary',
      };
    }
  }

  /**
   * Generate initial book and insight recommendations for transferred users
   * SINGLE MENTOR GATEWAY CALL: Extracts profile data + generates recommendations
   * Uses Phase 1 schema which extracts profile AND search query in one request
   */
  static async generateInitialRecommendations(
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get user profile with onboarding data
      const profileResult = await PrivateProfileService.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: 'Profile not found' };
      }

      const profile = profileResult.data;

      // Check if profile has onboarding Q&A to process
      if (!profile.onboardingAnswers?.answers) {
        return { success: false, error: 'No onboarding answers to process' };
      }

      console.log('📚 Generating profile + recommendations (single API call)...');

      const onboardingQA: OnboardingQA[] = profile.onboardingAnswers.answers.map((answer) => ({
        question: answer.questionText,
        answer: answer.answer,
      }));

      // Phase 1: ONE Mentor Gateway call extracts profile data + search query
      const phase1Messages = buildPhase1Messages(onboardingQA, profile.calculationResults);
      const phase1Data = await mentorChatService.sendStructuredRequest(
        phase1Messages,
        OnboardingPhase1Schema,
        { responseStyle: 'short', modelPreference: 'standard' }
      );

      console.log('✅ Phase 1 complete (extraction + search query):', {
        searchQuery: phase1Data.searchQuery,
        mainFocus: phase1Data.mainFocusLabel,
        goals: phase1Data.coreProfile?.goals?.length || 0,
        interests: phase1Data.coreProfile?.interests?.length || 0,
      });

      // Save extracted profile data from Phase 1
      const profileSaveResult = await PrivateProfileService.updateCoreProfile(userId, {
        mainFocusCategory: phase1Data.mainFocusCategory as any,
        mainFocusLabel: phase1Data.mainFocusLabel,
        goals: phase1Data.coreProfile.goals,
        interests: phase1Data.coreProfile.interests,
        challenges: phase1Data.coreProfile.challenges,
        personalityTraits: phase1Data.coreProfile.personalityTraits,
      }, 'ai_extraction:abbreviated_onboarding');

      if (!profileSaveResult.success) {
        console.warn('⚠️ Failed to save profile data:', profileSaveResult.error);
      }

      // Phase 2: Vector search (no API call, just Firestore query)
      const audiences = deriveAudiencesFromAnswers(onboardingQA);
      const vectorResults = await getVectorRecommendations(
        phase1Data.searchQuery,
        audiences
      );

      console.log('✅ Phase 2 complete (vector search):', {
        booksCount: vectorResults.recommendedBooks.length,
        insightsCount: vectorResults.insightPlan?.insights.length || 0,
      });

      // Format as DailyRecommendations
      const now = Date.now() / 1000;
      const today = new Date().toISOString().split('T')[0];
      
      const recommendations: DailyRecommendations = {
        date: today,
        personalizedMessage: vectorResults.personalizedMessage,
        recommendedBooks: vectorResults.recommendedBooks.map(book => ({
          ...book,
          recommendedAt: now,
        })),
        insightPlan: vectorResults.insightPlan ? {
          title: vectorResults.insightPlan.title,
          insights: vectorResults.insightPlan.insights.map(insight => ({
            ...insight,
            recommendedAt: now,
          })),
        } : undefined,
        query: vectorResults.query,
        generatedAt: now,
      };

      // Save recommendations to profile
      const saveResult = await PrivateProfileService.updateUserDailyRecommendations(
        userId,
        recommendations
      );

      if (!saveResult.success) {
        return { success: false, error: saveResult.error };
      }

      console.log('✅ Profile + recommendations saved successfully');
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error generating profile + recommendations:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate profile and recommendations',
      };
    }
  }

  /**
   * Extract profile data AND generate recommendations in ONE Mentor Gateway call
   * Convenience method for abbreviated onboarding
   * OPTIMIZED: No longer makes two separate API calls
   */
  static async extractAndGenerateRecommendations(
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    // Single call that does BOTH extraction and recommendations
    // Phase 1 schema extracts profile data + search query in one request
    return await this.generateInitialRecommendations(userId);
  }
}



